<script lang="ts">
  import { getToastStore } from '@skeletonlabs/skeleton';
  import Button from '$lib/components/common/button/Button.svelte';
  import DeleteConfirmModal from '$lib/components/drawers/client/ccr/components/DeleteConfirmModal.svelte';
  import { ccrStatementStore } from '$lib/stores/ccrStatementStore';
  import { ccrTransactionStore } from '$lib/stores/ccrTransactionStore';

  export let clientId: number;
  export let clientName: string = '';

  const toastStore = getToastStore();

  let showDeleteAllModal = false;
  let showDeleteBeforeModal = false;
  let showResetAllModal = false;
  let showResetBeforeModal = false;
  let deleteBeforeDate: string = '';
  let resetBeforeDate: string = '';
  let isDeleting = false;
  let isResetting = false;

  function formatDateForDisplay(dateStr: string): string {
    if (!dateStr) return '';
    const [yyyy, mm, dd] = dateStr.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }

  async function handleDeleteAll() {
    isDeleting = true;
    try {
      await ccrStatementStore.deleteAllStatements(clientId);
      toastStore.trigger({
        message: 'Successfully deleted statements',
        background: 'variant-filled-success',
      });
      showDeleteAllModal = false;
    } catch {
      // Error already shown by store
    } finally {
      isDeleting = false;
    }
  }

  async function handleDeleteBefore() {
    if (!deleteBeforeDate) return;
    isDeleting = true;
    try {
      await ccrStatementStore.deleteStatementsBefore(clientId, deleteBeforeDate);
      toastStore.trigger({
        message: 'Successfully deleted statements',
        background: 'variant-filled-success',
      });
      showDeleteBeforeModal = false;
      deleteBeforeDate = '';
    } catch {
      // Error already shown by store
    } finally {
      isDeleting = false;
    }
  }

  async function handleResetAll() {
    isResetting = true;
    try {
      await ccrTransactionStore.resetAllTransactions(clientId);
      toastStore.trigger({
        message: 'Successfully reset transactions',
        background: 'variant-filled-success',
      });
      showResetAllModal = false;
    } catch {
      // Error already shown by store
    } finally {
      isResetting = false;
    }
  }

  async function handleResetBefore() {
    if (!resetBeforeDate) return;
    isResetting = true;
    try {
      await ccrTransactionStore.resetTransactionsBefore(clientId, resetBeforeDate);
      toastStore.trigger({
        message: 'Successfully reset transactions',
        background: 'variant-filled-success',
      });
      showResetBeforeModal = false;
      resetBeforeDate = '';
    } catch {
      // Error already shown by store
    } finally {
      isResetting = false;
    }
  }
</script>

<div class="p-4 grid grid-cols-1 gap-5 xl:grid-cols-2">
  <!-- Delete all statements -->
  <div class="rounded-lg border border-gray-200 p-6">
    <h4 class="text-base font-semibold text-primary-500">Delete all statements</h4>
    <p class="mt-1 text-sm text-gray-500">Permanently removes all CC statements for this client. This action cannot be undone.</p>
    <div class="mt-4">
      <Button
        label="Delete all statements"
        variant="outlined"
        disabled={isDeleting}
        class="!border-error-500 !text-error-500 hover:!bg-error-500 hover:!text-white"
        onClick={() => (showDeleteAllModal = true)}
      />
    </div>
  </div>

  <!-- Delete statements before date -->
  <div class="rounded-lg border border-gray-200 p-6">
    <h4 class="text-base font-semibold text-primary-500">Delete statements before...</h4>
    <p class="mt-1 text-sm text-gray-500">Permanently removes all CC statements dated before the selected date. This action cannot be undone.</p>
    <div class="mt-4 flex items-center gap-4">
      <label class="text-sm text-gray-700" for="deleteBeforeDate">Delete statements before</label>
      <div class="relative inline-flex items-center rounded-md border border-gray-300 px-3 py-2 pr-8 text-sm">
        <span class={deleteBeforeDate ? 'text-gray-900' : 'text-gray-400'}>
          {deleteBeforeDate ? formatDateForDisplay(deleteBeforeDate) : 'dd/mm/yyyy'}
        </span>
        <input id="deleteBeforeDate" type="date" class="date-input-overlay" max={new Date().toISOString().split('T')[0]} bind:value={deleteBeforeDate} />
      </div>
      <Button
        label="Confirm delete"
        variant="outlined"
        disabled={!deleteBeforeDate || isDeleting}
        class={deleteBeforeDate ? '!border-error-500 !text-error-500 hover:!bg-error-500 hover:!text-white' : ''}
        onClick={() => (showDeleteBeforeModal = true)}
      />
    </div>
  </div>

  <!-- Reset all transactions -->
  <div class="rounded-lg border border-gray-200 p-6">
    <h4 class="text-base font-semibold text-primary-500">Reset all transactions</h4>
    <p class="mt-1 text-sm text-gray-500">Permanently reset all CC transactions for this client, ready to edit. This action cannot be undone.</p>
    <div class="mt-4">
        <Button
          label="Reset all transactions"
          variant="outlined"
          disabled={isResetting}
          class="!border-error-500 !text-error-500 hover:!bg-error-500 hover:!text-white"
          onClick={() => (showResetAllModal = true)}
        />
    </div>
  </div>

  <!-- Reset transactions before date -->
  <div class="rounded-lg border border-gray-200 p-6">
    <h4 class="text-base font-semibold text-primary-500">Reset transactions before...</h4>
    <p class="mt-1 text-sm text-gray-500">Permanently resets all CC transactions dated before the selected date. This action cannot be undone.</p>
    <div class="mt-4 flex items-center gap-4">
      <label class="text-sm text-gray-700" for="resetBeforeDate">Reset transactions before</label>
      <div class="relative inline-flex items-center rounded-md border border-gray-300 px-3 py-2 pr-8 text-sm">
        <span class={resetBeforeDate ? 'text-gray-900' : 'text-gray-400'}>
          {resetBeforeDate ? formatDateForDisplay(resetBeforeDate) : 'dd/mm/yyyy'}
        </span>
        <input id="resetBeforeDate" type="date" class="date-input-overlay" max={new Date().toISOString().split('T')[0]} bind:value={resetBeforeDate} />
      </div>
      <Button
        label="Confirm reset"
        variant="outlined"
        disabled={!resetBeforeDate || isResetting}
        class={resetBeforeDate ? '!border-error-500 !text-error-500 hover:!bg-error-500 hover:!text-white' : ''}
        onClick={() => (showResetBeforeModal = true)}
      />
    </div>
  </div>

</div>


<!-- Delete all confirmation modal -->
<DeleteConfirmModal bind:isOpen={showDeleteAllModal} title="Delete all statements?" on:confirm={handleDeleteAll} on:cancel={() => (showDeleteAllModal = false)}>
  <p>
    This will permanently delete <strong class="text-primary-500">all CC statements</strong> for
    {clientName || `client ${clientId}`}. This action cannot be undone.
  </p>
</DeleteConfirmModal>

<!-- Delete before date confirmation modal -->
<DeleteConfirmModal
  bind:isOpen={showDeleteBeforeModal}
  title="Delete statements before {deleteBeforeDate ? formatDateForDisplay(deleteBeforeDate) : ''}?"
  on:confirm={handleDeleteBefore}
  on:cancel={() => (showDeleteBeforeModal = false)}
>
  <p>
    This will permanently delete all CC statements dated before
    <strong class="text-primary-500">{deleteBeforeDate ? formatDateForDisplay(deleteBeforeDate) : ''}</strong> for
    {clientName || `client ${clientId}`}. This action cannot be undone.
  </p>
</DeleteConfirmModal>


<!-- Reset all confirmation modal -->
<DeleteConfirmModal bind:isOpen={showResetAllModal} title="Reset all transactions?" confirmTitle="Yes, reset" on:confirm={handleResetAll} on:cancel={() => (showResetAllModal = false)}>
    <p>
        This will permanently reset <strong class="text-primary-500">all CC transactions</strong> for
        {clientName || `client ${clientId}`}. This action cannot be undone.
    </p>
</DeleteConfirmModal>

<!-- Reset before date confirmation modal -->
<DeleteConfirmModal
  bind:isOpen={showResetBeforeModal}
  title="Reset transactions before {resetBeforeDate ? formatDateForDisplay(resetBeforeDate) : ''}?"
  confirmTitle="Yes, reset"
  on:confirm={handleResetBefore}
  on:cancel={() => (showResetBeforeModal = false)}
>
    <p>
        This will permanently reset all CC transactions dated before
        <strong class="text-primary-500">{resetBeforeDate ? formatDateForDisplay(resetBeforeDate) : ''}</strong> for
        {clientName || `client ${clientId}`}. This action cannot be undone.
    </p>
</DeleteConfirmModal>

<style>
  .date-input-overlay {
    position: absolute;
    inset: 0;
    width: 100%;
    cursor: pointer;
    color: transparent;
    background: transparent;
    border: none;
  }
  .date-input-overlay::-webkit-datetime-edit {
    color: transparent;
  }
  .date-input-overlay::-webkit-calendar-picker-indicator {
    position: absolute;
    right: 8px;
    cursor: pointer;
    opacity: 1;
  }
</style>
